import type User from '@this/domain/user/user2';
import type TravelerInformation from '@this/domain/traveler_information';
import type moment from 'moment';
import NonOrderItem from '@this/domain/non_order_item2';
import NonOrderItemPrice from '@this/domain/non_order_item_price2';
import NonOrderItemPriceDetail from '@this/domain/non_order_item_price_detail2';
import TransportElement from '@this/domain/transport_element2';
import TransportElementTerminal from '@this/domain/transport_element_terminal/transport_element_terminal2';
import HotelElement from '@this/domain/hotel_element2';
import TravelerList from '@this/src/domain/traveler/traveler_list';
import Traveler from '@this/src/domain/traveler/traveler';
import {
  convertTravelerListToTravelerInformations as convertTravelerListToTravelerInformationsBase,
  createInitialTravelers as createInitialTravelersBase
} from '@this/components/reserve_trip/select/arrangement_request_form/utils';

const cloneTransportElementTerminal = (terminal: TransportElementTerminal) =>
  new TransportElementTerminal({
    id: terminal.id,
    name: terminal.name,
    time: terminal.time.clone(),
    timeZone: terminal.timeZone,
    code: terminal.code
  });

const cloneTransportElement = (transport: TransportElement) =>
  new TransportElement({
    id: transport.id,
    name: transport.name,
    transportType: transport.transportType,
    from: cloneTransportElementTerminal(transport.from),
    to: cloneTransportElementTerminal(transport.to),
    boardingStation: transport.boardingStation,
    arrivalStation: transport.arrivalStation,
    airlineCode: transport.airlineCode,
    cabin: transport.cabin,
    carType: transport.carType,
    carTypeOther: transport.carTypeOther,
    railwaySeatType: transport.railwaySeatType,
    railwayTicketType: transport.railwayTicketType
  });

const cloneHotelElement = (hotel: HotelElement) =>
  new HotelElement({
    id: hotel.id,
    name: hotel.name,
    roomNum: hotel.roomNum,
    checkinDate: hotel.checkinDate.clone(),
    checkoutDate: hotel.checkoutDate.clone(),
    provider: hotel.provider,
    planName: hotel.planName,
    roomType: hotel.roomType,
    breakfast: hotel.breakfast,
    restricted: hotel.restricted,
    tooLate: hotel.tooLate,
    address: hotel.address,
    tel: hotel.tel,
    bedType: hotel.bedType,
    refundable: hotel.refundable
  });

export const cloneNonOrderItemForEditing = (item: NonOrderItem): NonOrderItem =>
  new NonOrderItem({
    id: item.id,
    index: item.index,
    price: new NonOrderItemPrice({
      id: item.price.id,
      price: item.price.price,
      details: item.price.details.map(
        detail => new NonOrderItemPriceDetail({ id: detail.id, price: detail.price })
      )
    }),
    peopleNum: item.peopleNum,
    nonOrderItemMappings: item.nonOrderItemMappings,
    transports: item.transports.map(cloneTransportElement),
    hotels: item.hotels.map(cloneHotelElement),
    paymentType: item.paymentType,
    requestType: item.requestType,
    isNeed: item.isNeed,
    supplier: item.supplier,
    tripType: item.tripType,
    tripTypeText: item.tripTypeText,
    elementType: item.elementType
  });

export const getNonOrderItemElementType = (item: NonOrderItem): string | null => {
  if (item.elementType) {
    return item.elementType;
  }
  const element = item.elements[0];
  if (!element) {
    return null;
  }
  if (element instanceof TransportElement) {
    return element.transportType;
  }
  if (element instanceof HotelElement) {
    return element.type;
  }
  return null;
};

export const createInitialTravelerInformations = (
  peoplenum: number,
  user: User,
  members: User[]
): TravelerInformation[] => {
  return createInitialTravelersBase(peoplenum, user, members);
};

export const convertTravelerListToTravelerInformations = (
  travelerList: TravelerList,
  peoplenum: number,
  user: User,
  members: User[]
): TravelerInformation[] => {
  return convertTravelerListToTravelerInformationsBase(travelerList.list, peoplenum, user, members);
};

export const convertTravelerInformationsToTravelerList = (
  travelers: TravelerInformation[],
  peoplenum: number,
  user: User,
  members: User[]
): TravelerList => {
  const travelerList = TravelerList.fromCount(peoplenum);
  const fallbackType = user.showOtherMembers && members.length > 0 ? 'member' : 'companion';

  travelers.forEach((traveler, index) => {
    if (!travelerList.list[index]) return;
    const type = traveler.type || (index === 0 ? 'self' : fallbackType);

    if (type === 'self') {
      const selfTraveler = new Traveler({
        id: user.id,
        name: user.name,
        email: user.email,
        employee_number: user.employeeNumber,
        department: user.department,
        type: 'self'
      });
      travelerList.setTravelerAtIndex(index, selfTraveler);
      return;
    }

    if (type === 'member') {
      const member = traveler.userId ? members.find(m => m.id === traveler.userId) : traveler.user;
      if (member) {
        const memberTraveler = new Traveler({
          id: member.id,
          name: member.name,
          email: member.email,
          employee_number: member.employeeNumber,
          department: member.department,
          type: 'member'
        });
        travelerList.setTravelerAtIndex(index, memberTraveler);
        return;
      }
    }

    const infoTraveler = new Traveler({
      id: traveler.id || '',
      user_id: traveler.userId,
      name: traveler.name || traveler.userName,
      last_name_roman: traveler.lastNameRoman,
      first_name_roman: traveler.firstNameRoman,
      middle_name_roman: traveler.middleNameRoman,
      last_name_kana: traveler.lastNameKana,
      first_name_kana: traveler.firstNameKana,
      birthday: traveler.birthday ? traveler.birthday.format('YYYY-MM-DD') : undefined,
      flight_last_name: traveler.flightLastName,
      flight_first_name: traveler.flightFirstName,
      flight_middle_name: traveler.flightMiddleName,
      flight_last_name_roman: traveler.flightLastNameRoman,
      flight_first_name_roman: traveler.flightFirstNameRoman,
      flight_middle_name_roman: traveler.flightMiddleNameRoman,
      flight_last_name_kana: traveler.flightLastNameKana,
      flight_first_name_kana: traveler.flightFirstNameKana,
      flight_birthday: traveler.flightBirthday ? traveler.flightBirthday.format('YYYY-MM-DD') : undefined,
      flight_gender: traveler.flightGender,
      flight_tel: traveler.flightTel,
      air_seat: traveler.airSeat,
      shinkansen_ticket_type: traveler.shinkansenTicketType,
      shinkansen_seat_type: traveler.shinkansenSeatType,
      shinkansen_seat: traveler.shinkansenSeat,
      request_shinkansen_seat: traveler.shinkansenSeatInfo,
      mileage_number: traveler.mileageNumber,
      exic_number: traveler.exicNumber,
      exic_password: traveler.exicPassword,
      exic_card_id: traveler.exicCardId,
      passport_number: traveler.passportNumber,
      passport_expire: traveler.passportExpire,
      passport_issued_country: traveler.passportIssuedCountry,
      immigration_card_creation: traveler.immigrationCardCreation,
      visa_application: traveler.visaApplication,
      nationality_id: traveler.nationalityId,
      type
    });

    travelerList.setTravelerAtIndex(index, infoTraveler);
  });

  return travelerList;
};

const formatMoment = (value?: moment.Moment) => value?.format('YYYY-MM-DD') || '';

export const areTravelerInformationsEqual = (
  left: TravelerInformation[],
  right: TravelerInformation[]
): boolean => {
  if (left.length !== right.length) return false;

  return left.every((traveler, index) => {
    const other = right[index];
    if (!other) return false;

    return (
      traveler.type === other.type &&
      traveler.userId === other.userId &&
      (traveler.name || '') === (other.name || '') &&
      (traveler.userName || '') === (other.userName || '') &&
      (traveler.lastNameRoman || '') === (other.lastNameRoman || '') &&
      (traveler.firstNameRoman || '') === (other.firstNameRoman || '') &&
      (traveler.middleNameRoman || '') === (other.middleNameRoman || '') &&
      (traveler.lastNameKana || '') === (other.lastNameKana || '') &&
      (traveler.firstNameKana || '') === (other.firstNameKana || '') &&
      formatMoment(traveler.birthday) === formatMoment(other.birthday) &&
      (traveler.flightGender || '') === (other.flightGender || '') &&
      (traveler.flightLastName || '') === (other.flightLastName || '') &&
      (traveler.flightFirstName || '') === (other.flightFirstName || '') &&
      (traveler.flightMiddleName || '') === (other.flightMiddleName || '') &&
      (traveler.flightLastNameKana || '') === (other.flightLastNameKana || '') &&
      (traveler.flightFirstNameKana || '') === (other.flightFirstNameKana || '') &&
      (traveler.flightLastNameRoman || '') === (other.flightLastNameRoman || '') &&
      (traveler.flightFirstNameRoman || '') === (other.flightFirstNameRoman || '') &&
      (traveler.flightMiddleNameRoman || '') === (other.flightMiddleNameRoman || '') &&
      formatMoment(traveler.flightBirthday) === formatMoment(other.flightBirthday)
    );
  });
};
